import { useMemo } from 'react';
import type { UseQueryState } from 'urql';
import { findCenterRegion } from '@sweetgreen/garnish';

import type { Query } from '@order/graphql';

import type {
  LocationQueryResult,
  LocationsQueryResult,
  LocationsResponse,
} from './types';

// ────────────────────────────────────────────────────────────────────────────────

export const useLocationsResponse = (
  result: UseQueryState,
  key: LocationsResponseQueryKey,
) => {
  const { data } = result;
  const response = data?.[key] ?? EMPTY_LOCATION_QUERY_RESULT;

  return useMemo(() => ({ ...result, response }), [response, result]);
};

// Extracts locations from query results.
export const useLocations = (result: LocationsQueryResult) => {
  const { response } = result;

  const locations = useMemo(
    () => response.filter(Boolean).map(({ location }) => location),
    [response],
  );

  return useMemo(() => ({ ...result, locations }), [locations, result]);
};

// Finds the center location.
export const useCenterLocations = (result: LocationsQueryResult) => {
  const response = useLocations(result);
  const center = findCenterRegion(response.locations);

  return useMemo<LocationsResponse>(
    () => ({ ...response, center }),
    [center, response],
  );
};

// ─── CONSTANTS ──────────────────────────────────────────────────────────────────

const EMPTY_LOCATION_QUERY_RESULT = [] as readonly LocationQueryResult[];

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type LocationsResponseQueryKey = keyof Pick<
  Query,
  'searchLocationsByBoundingBox' | 'searchLocationsByString'
>;
